import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Eye } from 'lucide-react';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { Control, Framework } from '@/types/soa';

export type SOAColumnsOptions = {
  helpData?: any;
  frameworks: Framework[];
  userIdToName: Record<string, string>;
  getStatusClass: (status: string) => string;
  isReadOnly?: boolean;
  onEdit: (control: Control) => void;
};

export const getSOAColumns = ({
  helpData,
  frameworks,
  userIdToName,
  getStatusClass,
  isReadOnly = false,
  onEdit,
}: SOAColumnsOptions): ColumnDef<Control, any>[] => {
  const frameworkMap = new Map<string, string>((frameworks || []).map(f => [f.id, f.name]));

  return [
    {
      header: () => (
        <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
          <HelpPopoverHover config={helpData?.['table.header.id']}>ID</HelpPopoverHover>
        </span>
      ),
      accessorKey: 'controlID',
      cell: ({ getValue }) => (
        <span className="font-medium whitespace-normal break-words text-center w-full">{getValue() as string}</span>
      ),
      meta: { headerClassName: 'w-[100px] text-center', cardLabel: 'ID', cellClassName: 'text-center'},
    },
    {
      header: () => (
        <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block whitespace-normal break-words">
          <HelpPopoverHover config={helpData?.['table.header.control']}>Control</HelpPopoverHover>
        </span>
      ),
      accessorKey: 'name',
      id: 'control',
      cell: ({ row }) => (
        <div className="max-w-md whitespace-normal">
          <div className="font-medium break-words">{row.original.name}</div>
          <div className="text-sm text-gray-600 break-words">{row.original.justification || row.original.description}</div>
        </div>
      ),
      meta: { cardLabel: 'Control' },
    },
    {
      header: () => (
        <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
          <HelpPopoverHover config={helpData?.['table.header.status']}>Status</HelpPopoverHover>
        </span>
      ),
      accessorKey: 'status',
      cell: ({ row }) => (
        <div className="flex items-center whitespace-nowrap justify-center text-center w-full">
          <span className={`status-indicator ${getStatusClass(row.original.status)}`}></span>
          <span>{row.original.status}</span>
        </div>
      ),
      meta: { cardLabel: 'Status', cellClassName: 'text-center', headerClassName: 'text-center' },
    },
    {
      header: () => (
        <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
          <HelpPopoverHover config={helpData?.['table.header.framework']}>Framework</HelpPopoverHover>
        </span>
      ),
      accessorKey: 'frameworkId',
      cell: ({ row }) => {
        const id = (row.original as Control).frameworkId || (row.original as Control).framework;
        const name = frameworkMap.get(id) || id || 'Unknown';
        return (
          <span className="framework-badge break-words text-center w-full">{name}</span>
        );
      },
      meta: { cardLabel: 'Framework', cellClassName: 'text-center', headerClassName: 'text-center' },
    },
    {
      header: () => (
        <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
          <HelpPopoverHover config={helpData?.['table.header.owner']}>Owner</HelpPopoverHover>
        </span>
      ),
      accessorKey: 'owner',
      cell: ({ row }) => (
        <span className="whitespace-normal text-center w-full">{(row.original.owner && userIdToName[row.original.owner]) || 'Not Assigned'}</span>
      ),
      meta: { cardLabel: 'Owner', cellClassName: 'text-center', headerClassName: 'text-center' },
    },
    {
      header: () => (
        <span className="bg-transparent px-2 py-1 rounded font-semibold text-center w-full block">
          <HelpPopoverHover config={helpData?.['table.header.actions']}>Actions</HelpPopoverHover>
        </span>
      ),
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex gap-1 justify-center text-center w-full">
          <Button variant="ghost" size="icon" onClick={() => onEdit(row.original)}>
            {isReadOnly ? <Eye className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
          </Button>
        </div>
      ),
      enableSorting: false,
      meta: { isAction: true, cardLabel: '', cellClassName: 'text-center', headerClassName: 'text-center' },
    },
  ];
};
